/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ActiveCell } from '../List';

import { keyExtractor } from '#utils/PRIVATE_collectionUtils';
import * as DomUtils from '#PRIVATE_TableCommon/utils/DomUtils';

export const LIST_ROW_SELECTOR = 'data-oj-list-row';
export const LIST_NODATA_SELECTOR = 'data-oj-list-nodata';
export const LIST_LOADING_SELECTOR = 'data-oj-list-loading';
export const LIST_LOADMORE_SELECTOR = 'data-oj-list-loadmore';
export const LIST_FOCUSABLE_SELECTOR = 'data-oj-list-focusable';

/**
 * Returns whether or not auto focus is enabled on the specified cell
 */
export const isAutoFocusCell = <K>(
  rootElement: HTMLElement,
  listId: string,
  activeCell: ActiveCell<K>
) => {
  const cellElement = getElementFromCell(rootElement, listId, activeCell);
  return cellElement != null && cellElement.dataset['ojListAutoFocus'] === 'enabled';
};

/**
 * Returns the key for the row at the given index
 */
export const getRowKeyForRowIndex = <K>(
  rootElement: HTMLElement,
  listId: string,
  index: number
) => {
  const rowElements = rootElement.querySelectorAll(`[${LIST_ROW_SELECTOR}='${listId}']`);
  if (rowElements.length > index) {
    return DomUtils.getRowKey<K>(rowElements[index] as HTMLElement);
  }
  return undefined;
};

/**
 * Returns the index of the given row element
 */
export const getRowIndexForRowElement = (
  rootElement: HTMLElement,
  listId: string,
  rowElement: HTMLElement
) => {
  return DomUtils.getRowIndexForRowElement(rootElement, listId, rowElement, LIST_ROW_SELECTOR);
};

/**
 * Returns the index of the row for the given key.
 */
export function getRowIndexForRowKey<K>(rootElement: HTMLElement, listId: string, rowKey: K) {
  const rowElements = rootElement.querySelectorAll(`[${LIST_ROW_SELECTOR}='${listId}']`);
  for (let i = 0; i < rowElements.length; i++) {
    if (rowKey === DomUtils.getRowKey(rowElements[i] as HTMLElement)) {
      return i;
    }
  }
  return undefined;
}

/**
 * Helper method to get the cell key definition from an element
 */
export function logicalCellExtractor<K extends string | number>(
  element: HTMLElement,
  listId: string,
  cellSelector?: string,
  onlyAncestors?: boolean
): ActiveCell<K> | undefined {
  const selectorString =
    cellSelector != null ? cellSelector : `[${LIST_FOCUSABLE_SELECTOR}='${listId}']`;
  const startingElement = onlyAncestors ? element.parentElement : element;
  if (startingElement != null) {
    const cellElement = startingElement.closest(selectorString) as HTMLElement;
    if (cellElement != null) {
      const cellType = DomUtils.getCellType(cellElement);
      if (cellType === 'data') {
        return {
          rowKey: keyExtractor<K>(cellElement, `[${LIST_ROW_SELECTOR}='${listId}']`)!,
          type: cellType
        };
      } else if (cellType === 'noData') {
        return { type: cellType };
      } else if (cellType === 'loading' || cellType === 'loadMore') {
        return { type: cellType };
      }
    }
  }
  return undefined;
}

export function rowExtractor(element: HTMLElement, listId: string): HTMLElement | undefined {
  const selectorString = `[${LIST_ROW_SELECTOR}='${listId}']`;
  return element.closest(selectorString) as HTMLElement;
}

/**
 * Helper method to get an element from a cell descriptor
 */
export function getElementFromCell<K>(
  rootElement: HTMLElement,
  listId: string,
  cell: ActiveCell<K>
): HTMLElement | undefined {
  if (cell.type === 'data') {
    // data body case
    const rowElements = rootElement.querySelectorAll(`[${LIST_ROW_SELECTOR}='${listId}']`);
    for (const rowElement of rowElements) {
      if (cell.rowKey === DomUtils.getRowKey(rowElement as HTMLElement)) {
        const cellElement = rowElement.querySelector(`[${LIST_FOCUSABLE_SELECTOR}='${listId}']`);
        if (cellElement != null) {
          return cellElement as HTMLElement;
        }
      }
    }
  } else if (cell.type === 'noData') {
    // no data case
    return getNoDataCellElement(rootElement, listId);
  } else if (cell.type === 'loading') {
    // loading skeletons case
    return getLoadingCellElement(rootElement, listId);
  } else if (cell.type === 'loadMore') {
    // loadMore skeletons case
    return getLoadMoreCellElement(rootElement, listId);
  }
  return undefined;
}

/**
 * Returns the key of the last focusable row in the List
 */
export const getLastRenderedRowKey = <K>(rootElement: HTMLElement, listId: string) => {
  return DomUtils.getLastRenderedRowKey<K>(rootElement, listId, LIST_ROW_SELECTOR);
};

/**
 * Returns the cell definition of the first rendered cell in the List
 */
export const getFirstFocusableCell = <K extends string | number>(
  rootElement: HTMLElement,
  listId: string
): ActiveCell<K> | undefined => {
  const firstCellElement = rootElement.querySelector(
    `[${LIST_FOCUSABLE_SELECTOR}='${listId}']`
  ) as HTMLElement;
  if (firstCellElement != null) {
    const firstCellType = DomUtils.getCellType(firstCellElement);
    if (firstCellType === 'noData') {
      return { type: 'noData' };
    }
    return {
      rowKey: keyExtractor<K>(firstCellElement, `[${LIST_ROW_SELECTOR}='${listId}']`)!,
      type: 'data'
    };
  }
  return undefined;
};

/**
 * Helper method to get the 'loading' cell
 */
export const getLoadingCellElement = (rootElement: HTMLElement, listId: string) => {
  return DomUtils.getElementBySelector(rootElement, listId, LIST_LOADING_SELECTOR);
};

/**
 * Helper method to get the 'loadMore' cell
 */
export const getLoadMoreCellElement = (rootElement: HTMLElement, listId: string) => {
  return DomUtils.getElementBySelector(rootElement, listId, LIST_LOADMORE_SELECTOR);
};

/**
 * Helper method to get the 'noData' cell
 */
export const getNoDataCellElement = (rootElement: HTMLElement, listId: string) => {
  return DomUtils.getElementBySelector(rootElement, listId, LIST_NODATA_SELECTOR);
};

/**
 * Helper method to determine if the 'loading' cell is present in the List
 */
export const hasLoadingCell = (rootElement: HTMLElement, listId: string) => {
  return getLoadingCellElement(rootElement, listId) != null;
};

/**
 * Helper method to determine if the 'loadMore' cell is present in the List
 */
export const hasLoadMoreCell = (rootElement: HTMLElement, listId: string) => {
  return getLoadMoreCellElement(rootElement, listId) != null;
};

/**
 * Helper method to determine if the no data cell is present in the List
 */
export const hasNoDataCell = (rootElement: HTMLElement, listId: string) => {
  return getNoDataCellElement(rootElement, listId) != null;
};
